import { Data, Match } from 'effect';
import React, { useContext } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { GetColor, TooltipPosition } from 'venn-ui-kit';
import { formatAllocation, type HistoricalPortfolioNodeAllocationType } from 'venn-utils';
import { useIsLeverageShortEnabled } from '../hooks/leverageAndShort';
import KeyBlockingNumericInput from '../key-blocking-numeric-input/KeyBlockingNumericInput';
import AllocationDifferenceTooltip from './AllocationDifferenceTooltip';
import { AllocationPanelContext } from './AllocationPanelContext';
import { ItemAllocationContext } from './contexts/ItemAllocationContext';
import { useItemAllocation, type UseItemAllocationProps } from './hooks/useItemAllocation';
import { Constants } from './Layout';

export const IncreasedSpecificityGhostFundWrapperClass = 'allocation-input-value-wrapper';
export const GhostFundAllocationInputClass = 'ghost-fund-allocation-input';
export const GhostFundAllocationValueClass = 'ghost-fund-allocation-value';
const InvalidRootValue = 'invalid-root-value';

// this interface is defined incorrectly. use ItemPercentageProps below instead.
export interface DeprecatedIncorrectlyDefinedItemPercentageProps {
  isPercentageMode: boolean;
  // For percentage calculation
  baseAllocation?: number;
  // For calculate delta
  orignalBaseAllocation?: number;
}

export type ItemPercentageProps =
  | {
      isPercentageMode: true;
      baseAllocation: number;
      originalBaseAllocation: number | undefined;
    }
  | {
      isPercentageMode: false;
    };

type ItemAllocationType = Data.TaggedEnum<{
  PublicHistorical: {
    allocationType: HistoricalPortfolioNodeAllocationType;
  };
  Private: {};
  Unknown: {}; // this is only unknown because of legacy code. if you figure out what the item type is, please update the call-site and this enum accordingly.
}>;
export const ItemAllocationType = Data.taggedEnum<ItemAllocationType>();

export interface ItemAllocationProps extends DeprecatedIncorrectlyDefinedItemPercentageProps {
  value?: number;
  originalValue?: number;
  isGhost: boolean;
  isStrategy: boolean;
  isRoot: boolean;
  isModified: boolean;
  isOutsideOfSelectedSubtree: boolean;
  onUpdateAllocation: (value: number) => void;
  usePortalForTooltip?: boolean;
  className?: string;
  isFillForward?: boolean;
  isMultiPortfolioUploader?: boolean;
  itemType: ItemAllocationType;
}
type ItemAllocationPrimitivesProviderProps = UseItemAllocationProps & { children: React.ReactNode };
type ItemAllocationPrimitivesRootProps = Pick<
  ItemAllocationProps,
  | 'value'
  | 'isModified'
  | 'isRoot'
  | 'isGhost'
  | 'isStrategy'
  | 'isOutsideOfSelectedSubtree'
  | 'usePortalForTooltip'
  | 'className'
  | 'isFillForward'
  | 'isMultiPortfolioUploader'
  | 'itemType'
> & { children: React.ReactNode };
type ItemAllocationPrimitivesInputProps = {
  isModified: boolean;
  isGhost: boolean;
  isStrategy: boolean;
  isPrivate: boolean;
};

export const ItemAllocationPrimitives = {
  Provider: ({
    isPercentageMode,
    baseAllocation,
    orignalBaseAllocation,
    value,
    isGhost,
    isStrategy,
    onUpdateAllocation,
    originalValue,
    isOutsideOfSelectedSubtree,
    isRoot,
    children,
    isFillForward,
    isMultiPortfolioUploader,
  }: ItemAllocationPrimitivesProviderProps) => {
    const contextValue = useItemAllocation({
      isPercentageMode,
      baseAllocation,
      orignalBaseAllocation,
      value,
      isGhost,
      isStrategy,
      onUpdateAllocation,
      originalValue,
      isOutsideOfSelectedSubtree,
      isRoot,
      isFillForward,
      isMultiPortfolioUploader,
    });
    return <ItemAllocationContext.Provider value={contextValue}>{children}</ItemAllocationContext.Provider>;
  },
  Root: ({
    value,
    isModified,
    isRoot,
    isGhost,
    isStrategy,
    isOutsideOfSelectedSubtree,
    usePortalForTooltip,
    className,
    children,
    isFillForward,
    isMultiPortfolioUploader,
    itemType,
  }: ItemAllocationPrimitivesRootProps) => {
    const {
      isEditing,
      isFocusedGhostInput,
      itemPercentageProps,
      canAddFundFromGhost,
      isNonGhostFundWithinSelectedSubtree,
      allocation,
      invalidRoot,
      difference,
    } = useContext(ItemAllocationContext);

    const historicalPortfolioNodeAllocationType = ItemAllocationType.$is('PublicHistorical')(itemType)
      ? itemType.allocationType
      : undefined;
    const { allocationPanelType } = useContext(AllocationPanelContext);
    const isHistoricalReadonly = allocationPanelType === 'historical read-only';

    const superScriptContent = Match.value(historicalPortfolioNodeAllocationType).pipe(
      Match.when('DRIFTED', () => '1'),
      Match.when('FORWARD_FILLED', () => '2'),
      Match.when('USER_UPLOADED', () => null),
      Match.when(undefined, () => (isFillForward ? '*' : null)),
      Match.exhaustive,
    );

    return (
      <div
        data-testid="qa-allocation-value"
        className={isMultiPortfolioUploader ? 'relative flex justify-end' : undefined}
      >
        <AllocationDifferenceTooltip
          className={`${className} ${isMultiPortfolioUploader ? 'flex justify-end' : ''}`}
          usePortal={usePortalForTooltip}
          hasDifference={isModified && !isEditing}
          value={allocation}
          difference={difference}
          position={isRoot ? TooltipPosition.Bottom : TooltipPosition.Top}
          isPercentageMode={itemPercentageProps.isPercentageMode}
          hidden={isEditing}
          isFillForward={isFillForward}
        >
          <ValueWrapper
            className={`${canAddFundFromGhost ? IncreasedSpecificityGhostFundWrapperClass : 'item-allocation-value-wrapper'} ${isMultiPortfolioUploader ? '!min-w-fit !w-fit' : ''}`}
            isRoot={isRoot}
            isStrategy={isStrategy}
            isModified={isModified && !isGhost}
            isFocusedGhostInput={isFocusedGhostInput}
            isGreyedOut={isOutsideOfSelectedSubtree || isGhost}
          >
            {(canAddFundFromGhost || isNonGhostFundWithinSelectedSubtree) && !isHistoricalReadonly && children}
            {(isGhost || isStrategy || isOutsideOfSelectedSubtree || isHistoricalReadonly) && (
              <Value
                className={
                  !isStrategy && isGhost ? GhostFundAllocationValueClass : invalidRoot ? InvalidRootValue : undefined
                }
              >
                {value !== null && value !== undefined
                  ? formatAllocation(
                      value,
                      isRoot,
                      itemPercentageProps.isPercentageMode,
                      itemPercentageProps.isPercentageMode ? itemPercentageProps.baseAllocation : undefined,
                    )
                  : '--'}
                {superScriptContent ? <sup data-testid="item-allocation-superscript">{superScriptContent}</sup> : null}
              </Value>
            )}
          </ValueWrapper>
        </AllocationDifferenceTooltip>
      </div>
    );
  },
  Input: ({ isModified, isGhost, isStrategy, isPrivate }: ItemAllocationPrimitivesInputProps) => {
    const { currentValue, itemPercentageProps, canAddFundFromGhost, onChange, onFocus, onBlur } =
      useContext(ItemAllocationContext);
    const { Colors } = useTheme();
    const isLeverageShortEnabled = useIsLeverageShortEnabled();
    return (
      <KeyBlockingNumericInput
        className={canAddFundFromGhost ? GhostFundAllocationInputClass : 'key-blocking-numeric-input'}
        isLocked={isGhost && isStrategy}
        value={currentValue}
        onChange={onChange}
        onFocus={onFocus}
        onCommitInput={onBlur}
        isPercentage={itemPercentageProps.isPercentageMode}
        height={20}
        defaultBorderColor={isModified ? Colors.HighlightDark : Colors.GreyScale.Grey40}
        textColor={isModified ? Colors.HighlightDark : Colors.Black}
        allowNegative={isLeverageShortEnabled && !isPrivate}
      />
    );
  },
  DisabledInput: ({ isModified }: Pick<ItemAllocationPrimitivesInputProps, 'isModified'>) => {
    const { currentValue, itemPercentageProps, canAddFundFromGhost, onChange, onFocus, onBlur } =
      useContext(ItemAllocationContext);
    const { Colors } = useTheme();
    return (
      <KeyBlockingNumericInput
        className={canAddFundFromGhost ? GhostFundAllocationInputClass : 'key-blocking-numeric-input'}
        isLocked
        value={currentValue}
        onChange={onChange}
        onFocus={onFocus}
        onCommitInput={onBlur}
        isPercentage={itemPercentageProps.isPercentageMode}
        height={20}
        defaultBorderColor={isModified ? Colors.HighlightDark : Colors.GreyScale.Grey40}
        textColor={isModified ? Colors.HighlightDark : Colors.Black}
        allowNegative={false}
      />
    );
  },
};
export const ItemAllocation = ({
  value,
  originalValue,
  isModified,
  isRoot,
  isGhost,
  isStrategy,
  isOutsideOfSelectedSubtree,
  onUpdateAllocation,
  usePortalForTooltip,
  className,
  isPercentageMode,
  baseAllocation,
  orignalBaseAllocation,
  isFillForward,
  isMultiPortfolioUploader,
  itemType,
}: ItemAllocationProps) => {
  return (
    <ItemAllocationPrimitives.Provider
      isPercentageMode={isPercentageMode}
      baseAllocation={baseAllocation}
      orignalBaseAllocation={orignalBaseAllocation}
      value={value}
      isGhost={isGhost}
      isStrategy={isStrategy}
      onUpdateAllocation={onUpdateAllocation}
      originalValue={originalValue}
      isOutsideOfSelectedSubtree={isOutsideOfSelectedSubtree}
      isRoot={isRoot}
      isFillForward={isFillForward}
      isMultiPortfolioUploader={isMultiPortfolioUploader}
    >
      <ItemAllocationPrimitives.Root
        value={value}
        isModified={isModified}
        isRoot={isRoot}
        isGhost={isGhost}
        isStrategy={isStrategy}
        isOutsideOfSelectedSubtree={isOutsideOfSelectedSubtree}
        usePortalForTooltip={usePortalForTooltip}
        className={className}
        isFillForward={isFillForward}
        isMultiPortfolioUploader={isMultiPortfolioUploader}
        itemType={itemType}
      >
        <ItemAllocationPrimitives.Input
          isModified={isModified}
          isGhost={isGhost}
          isStrategy={isStrategy}
          isPrivate={ItemAllocationType.$is('Private')(itemType)}
        />
      </ItemAllocationPrimitives.Root>
    </ItemAllocationPrimitives.Provider>
  );
};

function getTextColor(isModified: boolean, isGreyedOut: boolean) {
  if (isModified) {
    return isGreyedOut ? GetColor.HighlightLight : GetColor.HighlightDark;
  }
  return isGreyedOut ? GetColor.GreyScale.Grey40 : GetColor.Black;
}

export const ValueWrapper = styled.div<{
  isRoot: boolean;
  isStrategy: boolean;
  isModified: boolean;
  isFocusedGhostInput: boolean;
  isGreyedOut: boolean;
}>`
  ${({ isRoot }) => css`
    font-size: ${isRoot ? 16 : 14}px;
  `}
  ${({ isModified, isGreyedOut }) => css`
    color: ${getTextColor(isModified, isGreyedOut)};
  `}
  ${({ isStrategy }) =>
    isStrategy &&
    css`
      font-weight: bold;
    `}
  width: ${Constants.ALLOCATION_WIDTH}px;
  min-width: ${Constants.ALLOCATION_WIDTH}px;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-right: 5px;
  padding-left: 5px;
  ${({ isFocusedGhostInput }) => `
    .${GhostFundAllocationInputClass} {
      display: ${isFocusedGhostInput ? 'block' : 'none'};
    }
    .${GhostFundAllocationValueClass} {
      display: ${isFocusedGhostInput ? 'none' : 'block'};
    }
  `}
`;

export const Value = styled.div`
  padding-right: 5px;

  &.invalid-root-value {
    color: ${GetColor.Error};
    font-style: italic;
  }
`;
